import type React from 'react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type BrandingState,
  brandingPalette,
  type CurrentUser,
  type DomainLinkStatus,
  domainLinkStatus,
  type EmergencyContact,
  generateDomainLink,
  branding as loadBranding,
  type PaletteView,
  removeLogo,
  saveBranding,
  uploadLogo,
  type WidgetLayout,
  type WidgetPalette,
} from '../api.ts';
import { FieldHint } from '../components/FieldHint.tsx';
import { InfoTip } from '../components/InfoTip.tsx';
import { formatWhen } from '../format.ts';
import { useAnnounce } from '../hooks/useAnnounce.tsx';
import { apiMessage, errorMessage } from '../messages.ts';

/**
 * Il limite di dimensione in ingresso, ripetuto qui.
 *
 * Il server lo applica comunque — questo valore non è una difesa, è la
 * differenza fra "il file è troppo grande" detto subito, appena scelto, e
 * detto dopo un caricamento intero mandato e rifiutato. Deve restare uguale a
 * `LOGO_MAX_UPLOAD_BYTES` in `apps/api/src/lib/logo-storage.ts`
 * (`docs/DECISIONI.md`, AZ11).
 */
const LOGO_MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

/**
 * L'aspetto del widget, dalla parte di chi lo decide (sezione 4).
 *
 * Fino a ieri il colore di un ente si cambiava con una `update` a mano sul
 * database: i campi c'erano, il widget li leggeva, e nessuna schermata li
 * scriveva. Per chi compra è il primo scoglio del primo giorno, ed è anche il
 * primo posto in cui un prodotto multi-tenant sembra o non sembra suo.
 *
 * Due scelte governano tutta la schermata.
 *
 * **L'anteprima non è un ornamento.** Cambiare un colore alla cieca e poi
 * andare a ricaricare il proprio sito per vedere l'effetto non è una funzione,
 * è un compito che diamo al cliente. Qui, accanto ai campi, c'è il pannello
 * disegnato con i testi e i colori che si stanno scrivendo — e c'è **due
 * volte**, chiaro e scuro, perché il widget vive dentro il sito di qualcun
 * altro e quel sito può essere in tema scuro.
 *
 * **La correzione del contrasto si dice a parole.** La piattaforma corregge
 * l'accento quando non raggiunge la soglia, e mostrarlo con due quadratini
 * colorati affiderebbe l'unica informazione che conta al colore stesso: a chi
 * non vede non direbbe niente, e a chi vede non direbbe **perché**. Per questo
 * accanto a ogni palette c'è scritto il colore scelto, quello usato, il
 * rapporto di contrasto raggiunto e la ragione dello spostamento.
 *
 * I colori non si derivano qui: si chiedono al server, che è lo stesso che
 * risponde al widget. Una seconda copia di `derivePalette` nella console
 * sarebbe una schermata che promette un colore e un sito che ne mostra un
 * altro il giorno in cui le due copie divergono.
 *
 * L'anteprima è fatta di testo, non di controlli: dentro non c'è nessun
 * `button`. Un pulsante finto sarebbe una tappa in più nel percorso da
 * tastiera e un bersaglio che non fa niente per chi lo tocca.
 */

type Props = { user: CurrentUser; headingRef?: React.RefObject<HTMLHeadingElement | null> };

/** Le sei caselle, nell'ordine in cui compaiono nel pannello. */
const TEXT_LABELS: Record<string, string> = {
  'widget.title': 'branding.text_widget_title',
  'widget.subtitle': 'branding.text_widget_subtitle',
  'widget.open.button': 'branding.text_button_audio',
  'widget.open.video': 'branding.text_button_video',
  'widget.open.chat': 'branding.text_button_chat',
  'widget.open.hint': 'branding.text_note',
};

/** Il canale a cui appartiene l'etichetta: serve all'anteprima, non al modulo. */
const CHANNEL_TEXTS: Record<string, string> = {
  audio: 'widget.open.button',
  video: 'widget.open.video',
  chat: 'widget.open.chat',
};

const HEX = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/**
 * Alias, non solo comodità: `(event: React.ChangeEvent<HTMLInputElement>): Promise<void>`
 * sulla stessa riga fa scattare il test dei vincoli che cerca testo fra `>` e
 * `<` nel JSX (due generici, uno di seguito all'altro, sembrano un tag
 * chiuso). Vero anche con `event` su una riga propria: il formattatore di
 * Biome la riaccorpa. L'alias è l'unica forma che soddisfa entrambi.
 */
type FileInputChangeEvent = React.ChangeEvent<HTMLInputElement>;

type ContactDraft = EmergencyContact & { id: string };

type Draft = {
  accentColor: string;
  layout: WidgetLayout;
  channels: string[];
  texts: Record<string, Record<string, string>>;
  contacts: ContactDraft[];
  defaultLocale: string;
  /** Più di una lingua attiva: il widget rileva quella di chi visita. */
  autoDetectLocale: boolean;
};

let nextContactId = 0;

function toDraft(state: BrandingState): Draft {
  return {
    accentColor: state.accentColor,
    layout: state.layout,
    channels: [...state.channels],
    texts: Object.fromEntries(
      Object.entries(state.texts).map(([locale, values]) => [locale, { ...values }]),
    ),
    contacts: state.emergency.own.map((contact) => {
      nextContactId += 1;
      return { id: `c${nextContactId}`, number: contact.number, labels: { ...contact.labels } };
    }),
    defaultLocale: state.defaultLocale,
    autoDetectLocale: state.locales.length > 1,
  };
}

export function WidgetPage({ user, headingRef }: Props) {
  const { t } = useTranslation();
  const [state, setState] = useState<BrandingState>();
  const [draft, setDraft] = useState<Draft>();
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [locale, setLocale] = useState<string>();
  /** La palette del colore che si sta scrivendo, finché non lo si salva. */
  const [preview, setPreview] = useState<PaletteView>();
  /** Il logo si carica subito, non con il resto del modulo (sezione 4). */
  const [logoBusy, setLogoBusy] = useState(false);
  const logoInputId = useId();
  /**
   * Dove va il fuoco dopo "Togli il logo".
   *
   * Il bottone sparisce dal DOM appena il logo non c'è più: senza spostare il
   * fuoco a mano finirebbe sul `<body>`, e chi lavora da tastiera perderebbe
   * il punto in cui si trovava proprio mentre arriva l'annuncio "Logo tolto"
   * in una regione live lontana (`accessibility`, WCAG 2.4.3/G199). Il campo
   * di caricamento è il punto operativo più sensato: è lì che si ricomincia.
   */
  const logoInputRef = useRef<HTMLInputElement>(null);
  const { say, announcer } = useAnnounce();

  const load = useCallback(async () => {
    try {
      const next = await loadBranding();
      setState(next);
      setDraft(toDraft(next));
      setPreview(next);
      setLocale((current) => current ?? next.defaultLocale);
      setFailed(false);
    } catch (error) {
      // Senza uno stato d'errore la schermata mostrerebbe un modulo vuoto e
      // un'anteprima bianca, cioè un ente senza configurazione: due
      // affermazioni false al posto di "non l'abbiamo letta".
      setFailed(true);
      say(errorMessage(t, error), 'danger');
    }
  }, [say, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const accent = draft?.accentColor ?? '';
  const accentValid = HEX.test(accent);

  /**
   * La palette del colore che si sta scrivendo, chiesta al server.
   *
   * Con un ritardo: la casella dei colori del browser emette un valore per
   * ogni pixel di trascinamento, e senza attesa sarebbero duecento richieste
   * per scegliere un blu. Duecentocinquanta millisecondi è il tempo in cui una
   * mano si ferma, e ogni richiesta annulla la precedente — l'anteprima deve
   * mostrare l'**ultimo** colore scelto, non quello arrivato per ultimo.
   */
  useEffect(() => {
    if (!accentValid) return;
    const controller = new AbortController();
    const timer = globalThis.setTimeout(() => {
      brandingPalette(accent, { signal: controller.signal })
        .then((next) => {
          if (!controller.signal.aborted) setPreview(next);
        })
        .catch(() => {
          // Un colore che il server rifiuta lascia in piedi l'ultima anteprima
          // valida: cancellarla mostrerebbe un pannello senza colori, che non
          // è ciò che l'ente sta configurando.
        });
    }, 250);

    return () => {
      controller.abort();
      globalThis.clearTimeout(timer);
    };
  }, [accent, accentValid]);

  const update = (patch: Partial<Draft>): void => {
    setDraft((current) => (current ? { ...current, ...patch } : current));
  };

  const setText = (key: string, value: string): void => {
    setDraft((current) => {
      if (!current || !locale) return current;
      const values = { ...(current.texts[locale] ?? {}), [key]: value };
      return { ...current, texts: { ...current.texts, [locale]: values } };
    });
  };

  const setContact = (id: string, patch: Partial<EmergencyContact>): void => {
    setDraft((current) =>
      current
        ? {
            ...current,
            contacts: current.contacts.map((contact) =>
              contact.id === id ? { ...contact, ...patch } : contact,
            ),
          }
        : current,
    );
  };

  /** Dove portare il fuoco dopo aver aggiunto o tolto un recapito. */
  const contactsRef = useRef<HTMLUListElement>(null);
  const focusAfter = useRef<string | undefined>(undefined);

  useEffect(() => {
    // Si aspetta il ridisegno: il campo appena aggiunto non esiste ancora nel
    // momento in cui lo stato cambia.
    const target = draft && focusAfter.current;
    if (!target) return;
    focusAfter.current = undefined;
    contactsRef.current?.querySelector<HTMLElement>(`#contact-number-${target}`)?.focus();
  }, [draft]);

  const addContact = (): void => {
    if (!draft || !state || !locale) return;
    if (draft.contacts.length >= state.emergency.max) {
      say(t('branding.emergency_full', { count: state.emergency.max }), 'danger');
      return;
    }
    nextContactId += 1;
    const id = `c${nextContactId}`;
    update({ contacts: [...draft.contacts, { id, number: '', labels: {} }] });
    focusAfter.current = id;
    say(t('branding.emergency_added'));
  };

  const removeContact = (id: string): void => {
    if (!draft) return;
    update({ contacts: draft.contacts.filter((contact) => contact.id !== id) });
    say(t('branding.emergency_removed'));
  };

  const save = async (): Promise<void> => {
    if (!draft || saving) return;
    setSaving(true);
    try {
      const next = await saveBranding({
        accentColor: draft.accentColor,
        layout: draft.layout,
        channels: draft.channels,
        texts: draft.texts,
        emergencyContacts: draft.contacts.map((contact) => ({
          number: contact.number,
          labels: contact.labels,
        })),
        defaultLocale: draft.defaultLocale,
        autoDetectLocale: draft.autoDetectLocale,
      });
      setState(next);
      setDraft(toDraft(next));
      setPreview(next);
      say(t('branding.saved'));
    } catch (error) {
      say(errorMessage(t, error), 'danger');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Il logo si carica **subito**, non con "Salva".
   *
   * È un file, non un campo di testo: non c'è un'anteprima intermedia che
   * valga la pena costruire, e trattarlo come gli altri campi del modulo
   * vorrebbe dire poter perdere un caricamento riuscito annullando le
   * modifiche non salvate del resto della schermata. Il server lo scrive e lo
   * ricodifica nella stessa richiesta: quando risponde, è già il logo vero.
   */
  const handleLogoChange = async (event: FileInputChangeEvent): Promise<void> => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || logoBusy) return;

    if (file.size > LOGO_MAX_UPLOAD_BYTES) {
      say(apiMessage(t, 'errors.logo_too_large'), 'danger');
      return;
    }

    setLogoBusy(true);
    try {
      const next = await uploadLogo(file);
      setState(next);
      setPreview(next);
      say(t('branding.logo_saved'));
    } catch (error) {
      say(errorMessage(t, error), 'danger');
    } finally {
      setLogoBusy(false);
    }
  };

  const handleLogoRemove = async (): Promise<void> => {
    if (logoBusy) return;
    setLogoBusy(true);
    try {
      const next = await removeLogo();
      setState(next);
      setPreview(next);
      say(t('branding.logo_removed'));
      // Il bottone appena premuto sta per sparire dal DOM (non c'è più un
      // logo da togliere): il fuoco torna al campo di caricamento invece di
      // cadere sul `<body>`.
      logoInputRef.current?.focus();
    } catch (error) {
      say(errorMessage(t, error), 'danger');
    } finally {
      setLogoBusy(false);
    }
  };

  const editable = state?.editable === true;
  const chosen = accentValid ? normalize(accent) : undefined;

  return (
    <main className="page" id="main" tabIndex={-1}>
      <h2 ref={headingRef} tabIndex={-1}>
        {t('branding.title')}
      </h2>

      {announcer}

      {failed ? (
        <p>
          <button type="button" className="button" onClick={() => void load()}>
            {t('branding.retry')}
          </button>
        </p>
      ) : null}

      {!state || !draft || !locale ? (
        <div className="card stack">
          <p className="skeleton skeleton--wide" />
          <p className="skeleton" />
          <p className="skeleton skeleton--half" />
        </div>
      ) : (
        <div className="branding-layout">
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              void save();
            }}
          >
            {!editable ? <p className="note">{t('branding.read_only')}</p> : null}

            {/* ------------------------------------------------------ colore */}
            {/* --------------------------------------------------------- logo */}
            <section className="card stack" aria-labelledby="branding-logo">
              <div className="section-head">
                <div className="section-head__title">
                  <h3 id="branding-logo">{t('branding.logo_title')}</h3>
                  <InfoTip>{t('branding.logo_sub')}</InfoTip>
                </div>
              </div>

              <div className="cluster">
                {state.logoUrl ? (
                  <img
                    className="branding-logo-preview"
                    src={state.logoUrl}
                    alt=""
                    width={64}
                    height={64}
                  />
                ) : (
                  <p
                    className="branding-logo-preview branding-logo-preview--empty"
                    aria-hidden="true"
                  >
                    {(state.name || user.tenant?.name || '').slice(0, 2).toUpperCase()}
                  </p>
                )}

                <div className="field">
                  <div className="field__label-row">
                    <label htmlFor={logoInputId}>
                      {t(state.logoUrl ? 'branding.logo_replace' : 'branding.logo_upload')}
                    </label>
                    <FieldHint
                      id="branding-logo-hint"
                      label={t(state.logoUrl ? 'branding.logo_replace' : 'branding.logo_upload')}
                    >
                      {t('branding.logo_hint', { size: '5 MB' })}
                    </FieldHint>
                  </div>
                  <input
                    ref={logoInputRef}
                    id={logoInputId}
                    type="file"
                    accept="image/*"
                    aria-describedby="branding-logo-hint"
                    disabled={!editable || logoBusy}
                    onChange={(event) => void handleLogoChange(event)}
                  />
                </div>

                {state.logoUrl ? (
                  <button
                    type="button"
                    className="button button--danger"
                    disabled={!editable || logoBusy}
                    onClick={() => void handleLogoRemove()}
                  >
                    {t('branding.logo_remove')}
                  </button>
                ) : null}
              </div>
            </section>

            <section className="card stack" aria-labelledby="branding-color">
              <div className="section-head">
                <div className="section-head__title">
                  <h3 id="branding-color">{t('branding.color_title')}</h3>
                  <InfoTip>{t('branding.color_sub')}</InfoTip>
                </div>
              </div>

              <div className="cluster">
                <div className="field field--inline">
                  <label htmlFor="branding-accent-pick">{t('branding.color_pick')}</label>
                  <input
                    id="branding-accent-pick"
                    type="color"
                    disabled={!editable}
                    value={chosen ?? '#000000'}
                    onChange={(event) => update({ accentColor: event.target.value })}
                  />
                </div>
                <div className="field field--inline">
                  <label htmlFor="branding-accent">{t('branding.color_hex')}</label>
                  <input
                    id="branding-accent"
                    type="text"
                    inputMode="text"
                    spellCheck={false}
                    disabled={!editable}
                    aria-invalid={accentValid ? undefined : 'true'}
                    aria-describedby={accentValid ? undefined : 'branding-accent-error'}
                    value={draft.accentColor}
                    onChange={(event) => update({ accentColor: event.target.value })}
                  />
                </div>
              </div>
              {!accentValid ? (
                <p className="banner banner--danger" id="branding-accent-error">
                  {t('branding.color_invalid')}
                </p>
              ) : null}

              {preview ? (
                <PaletteReport view={preview} chosen={chosen ?? draft.accentColor} />
              ) : null}
            </section>

            {/* ------------------------------------------------------- testi */}
            <section className="card stack" aria-labelledby="branding-texts">
              <div className="section-head">
                <div className="section-head__title">
                  <h3 id="branding-texts">{t('branding.texts_title')}</h3>
                  <InfoTip>{t('branding.texts_sub')}</InfoTip>
                </div>
                {state.locales.length > 1 ? (
                  <div className="field field--inline">
                    <label htmlFor="branding-locale">{t('branding.locale_label')}</label>
                    <select
                      id="branding-locale"
                      value={locale}
                      onChange={(event) => setLocale(event.target.value)}
                    >
                      {state.locales.map((entry) => (
                        <option key={entry} value={entry}>
                          {t(`language.${entry}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
              </div>

              {state.fields.map((field) => {
                const value = draft.texts[locale]?.[field.key] ?? '';
                const fallback = state.defaults[locale]?.[field.key] ?? '';
                return (
                  <div className="field" key={field.key}>
                    <label htmlFor={`text-${field.key}`}>{t(TEXT_LABELS[field.key] ?? '')}</label>
                    <input
                      id={`text-${field.key}`}
                      type="text"
                      disabled={!editable}
                      maxLength={field.maxLength}
                      aria-describedby={`hint-${field.key}`}
                      value={value}
                      onChange={(event) => setText(field.key, event.target.value)}
                    />
                    <p className="muted" id={`hint-${field.key}`}>
                      {t('branding.text_count', { count: value.length, max: field.maxLength })}
                      {' · '}
                      {fallback
                        ? t('branding.text_default', { text: fallback })
                        : t('branding.text_default_none')}
                    </p>
                  </div>
                );
              })}

              <p className="note">{t('branding.text_safety_note')}</p>
            </section>

            {/* -------------------------------------------- canali e ingombro */}
            <section className="card stack" aria-labelledby="branding-channels">
              <h3 id="branding-channels">{t('branding.channels_title')}</h3>

              <fieldset className="field-group" disabled={!editable}>
                <legend>{t('branding.channels_label')}</legend>
                {state.availableChannels.map((channel) => (
                  <div className="field field--check" key={channel}>
                    <input
                      id={`channel-${channel}`}
                      type="checkbox"
                      checked={draft.channels.includes(channel)}
                      onChange={(event) =>
                        update({
                          channels: event.target.checked
                            ? [...draft.channels, channel]
                            : draft.channels.filter((entry) => entry !== channel),
                        })
                      }
                    />
                    <label htmlFor={`channel-${channel}`}>{t(`branding.channel_${channel}`)}</label>
                  </div>
                ))}
                <p className="muted">{t('branding.channels_hint')}</p>
              </fieldset>

              <fieldset className="field-group" disabled={!editable}>
                <legend>{t('branding.layout_label')}</legend>
                {state.layouts.map((entry) => (
                  <div className="field field--check" key={entry}>
                    <input
                      id={`layout-${entry}`}
                      type="radio"
                      name="branding-layout"
                      checked={draft.layout === entry}
                      onChange={() => update({ layout: entry })}
                    />
                    <label htmlFor={`layout-${entry}`}>{t(`branding.layout_${entry}`)}</label>
                  </div>
                ))}
              </fieldset>
            </section>

            {/* -------------------------------------------- lingua del sito */}
            <section className="card stack" aria-labelledby="branding-site-locale">
              <div className="section-head">
                <div className="section-head__title">
                  <h3 id="branding-site-locale">{t('branding.site_locale_title')}</h3>
                  <InfoTip>{t('branding.site_locale_sub')}</InfoTip>
                </div>
              </div>

              <div className="field">
                <label htmlFor="site-locale">{t('branding.site_locale_label')}</label>
                <select
                  id="site-locale"
                  disabled={!editable}
                  value={draft.defaultLocale}
                  onChange={(event) => update({ defaultLocale: event.target.value })}
                >
                  {state.supportedLocales.map((entry) => (
                    <option key={entry} value={entry}>
                      {t(`language.${entry}`)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field field--inline field--check">
                <input
                  type="checkbox"
                  id="site-locale-auto"
                  disabled={!editable}
                  checked={draft.autoDetectLocale}
                  aria-describedby="site-locale-auto-hint"
                  onChange={(event) => update({ autoDetectLocale: event.target.checked })}
                />
                <label htmlFor="site-locale-auto">{t('branding.site_locale_auto_label')}</label>
              </div>
              <p className="muted" id="site-locale-auto-hint">
                {draft.autoDetectLocale
                  ? t('branding.site_locale_auto_hint', {
                      locale: t(`language.${draft.defaultLocale}`),
                    })
                  : t('branding.site_locale_fixed_hint', {
                      locale: t(`language.${draft.defaultLocale}`),
                    })}
              </p>
            </section>

            {/* --------------------------------------------------- emergenze */}
            <section className="card stack" aria-labelledby="branding-emergency">
              <h3 id="branding-emergency">{t('branding.emergency_title')}</h3>

              <h4>{t('branding.emergency_fixed')}</h4>
              <ul className="plain-list">
                {state.emergency.fixed.map((entry) => (
                  <li className="row" key={entry.number}>
                    <p className="row__title">{entry.number}</p>
                    <p className="row__meta">
                      {entry.labelKey ? t(`emergency:${entry.labelKey}`) : entry.number}
                    </p>
                  </li>
                ))}
              </ul>
              <p className="note">{t('branding.emergency_fixed_note')}</p>

              <div className="section-head">
                <h4>{t('branding.emergency_own')}</h4>
                <span className="count">{draft.contacts.length}</span>
              </div>

              {draft.contacts.length === 0 ? (
                <p className="empty">{t('branding.emergency_none')}</p>
              ) : (
                <ul className="plain-list" ref={contactsRef}>
                  {draft.contacts.map((contact) => (
                    <li className="step-row" key={contact.id}>
                      <div className="field">
                        <label htmlFor={`contact-number-${contact.id}`}>
                          {t('branding.emergency_number')}
                        </label>
                        <input
                          id={`contact-number-${contact.id}`}
                          type="text"
                          inputMode="tel"
                          disabled={!editable}
                          value={contact.number}
                          onChange={(event) =>
                            setContact(contact.id, { number: event.target.value })
                          }
                        />
                      </div>
                      <div className="field">
                        <label htmlFor={`contact-label-${contact.id}`}>
                          {t('branding.emergency_label')}
                        </label>
                        <input
                          id={`contact-label-${contact.id}`}
                          type="text"
                          disabled={!editable}
                          maxLength={state.emergency.labelMaxLength}
                          value={contact.labels[locale] ?? ''}
                          onChange={(event) =>
                            setContact(contact.id, {
                              labels: { ...contact.labels, [locale]: event.target.value },
                            })
                          }
                        />
                      </div>
                      <button
                        type="button"
                        className="button button--danger"
                        disabled={!editable}
                        aria-label={t('branding.emergency_remove', {
                          number: contact.number || t('branding.emergency_number'),
                        })}
                        onClick={() => removeContact(contact.id)}
                      >
                        {t('branding.emergency_remove_short')}
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <p className="actions">
                <button
                  type="button"
                  className="button"
                  disabled={!editable || draft.contacts.length >= state.emergency.max}
                  onClick={addContact}
                >
                  {t('branding.emergency_add')}
                </button>
              </p>
            </section>

            <p className="actions actions--end">
              <button
                type="submit"
                className="button button--primary"
                disabled={!editable || saving || !accentValid}
              >
                {t('branding.save')}
              </button>
            </p>
          </form>

          {/* ---------------------------------------------------- anteprima */}
          <section className="card stack" aria-labelledby="branding-preview">
            <div className="section-head">
              <div className="section-head__title">
                <h3 id="branding-preview">{t('branding.preview_title')}</h3>
                <InfoTip>{t('branding.preview_sub')}</InfoTip>
              </div>
            </div>

            {preview ? (
              <>
                <Preview
                  dark={false}
                  title={t('branding.preview_light')}
                  state={state}
                  draft={draft}
                  locale={locale}
                  palette={preview.palette}
                  name={state.name || user.tenant?.name || ''}
                />
                <Preview
                  dark
                  title={t('branding.preview_dark')}
                  state={state}
                  draft={draft}
                  locale={locale}
                  palette={preview.palette}
                  name={state.name || user.tenant?.name || ''}
                />
              </>
            ) : (
              <p className="skeleton skeleton--wide" />
            )}
          </section>
        </div>
      )}

      <DomainLinkSection />
    </main>
  );
}

/**
 * Il collegamento per il referente tecnico (mock 5, `o-tech`).
 *
 * Indipendente dal modulo dell'aspetto: non salva insieme a colore e testi, ha
 * il proprio pulsante e la propria richiesta. Compare comunque su questa
 * schermata perché è la stessa famiglia di domanda — "come faccio arrivare il
 * mio sito al widget?" — e non un'altra sezione da cercare altrove.
 *
 * Solo il coordinatore genera: il fornitore in assistenza vede lo stato in
 * sola lettura, come le altre schermate di `adminContext`, e se prova a
 * generare riceve `errors.domain_link_read_only` dal server — la stessa forma
 * di gate già usata per il salvataggio dell'aspetto (`admin-branding.ts`), non
 * un pulsante nascosto: nasconderlo qui vorrebbe dire indovinare lato client
 * un permesso che il server già sa dire con precisione.
 */
function DomainLinkSection() {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState<DomainLinkStatus>();
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<{ url: string; expiresAt: string }>();
  const { say, announcer } = useAnnounce();

  useEffect(() => {
    const controller = new AbortController();
    void domainLinkStatus()
      .then((next) => {
        if (controller.signal.aborted) return;
        setStatus(next);
        setFailed(false);
      })
      .catch(() => {
        if (!controller.signal.aborted) setFailed(true);
      });
    return () => controller.abort();
  }, []);

  const generate = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      const result = await generateDomainLink();
      setIssued({ url: result.url, expiresAt: result.expiresAt });
      setStatus({ active: true, expiresAt: result.expiresAt, domainConfigured: true });
      say(t('domain_link.admin_created_hint'));
    } catch (error) {
      say(`${t('domain_link.admin_failed')} ${errorMessage(t, error)}`, 'danger');
    } finally {
      setBusy(false);
    }
  };

  const copy = async (url: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
      say(t('domain_link.admin_copied'));
    } catch {
      // Il collegamento resta comunque a schermo, selezionabile a mano — ma
      // chi non vede lo schermo deve saperlo: senza questo annuncio restava
      // in attesa di una copia che non era mai avvenuta, senza nessun segnale
      // (sezione 7bis, WCAG 4.1.3).
      say(t('domain_link.admin_copy_failed'), 'danger');
    }
  };

  return (
    <section className="card stack" aria-labelledby="domain-link-title">
      <div className="section-head">
        <div className="section-head__title">
          <h3 id="domain-link-title">{t('domain_link.admin_title')}</h3>
          <InfoTip>{t('domain_link.admin_hint')}</InfoTip>
        </div>
      </div>

      {announcer}

      {!status && !failed ? (
        <>
          {/*
            La barra grigia è decorativa: chi non vede lo schermo non deve
            sentire un paragrafo vuoto, deve sentire che la sezione sta ancora
            caricando (stesso pattern di `HoursPage` e di `Loading` in
            `AdminPage`, AM4).
          */}
          <p className="visually-hidden">{t('admin.loading')}</p>
          <p className="skeleton skeleton--half" aria-hidden="true" />
        </>
      ) : null}
      {failed ? <p className="banner banner--danger">{t('domain_link.admin_failed')}</p> : null}

      {status && !status.domainConfigured ? (
        <p className="note">{t('domain_link.admin_no_domain')}</p>
      ) : null}

      {status?.domainConfigured ? (
        <>
          <p className="muted">
            {status.active && status.expiresAt
              ? t('domain_link.admin_active', {
                  when: formatWhen(status.expiresAt, i18n.resolvedLanguage, undefined),
                })
              : t('domain_link.admin_inactive')}
          </p>

          <p className="actions">
            <button
              type="button"
              className="button"
              disabled={busy}
              onClick={() => void generate()}
            >
              {busy ? t('domain_link.admin_loading') : t('domain_link.admin_generate')}
            </button>
          </p>

          {issued ? (
            <div className="field">
              <label htmlFor="domain-link-url">{t('domain_link.admin_title')}</label>
              <div className="field field--inline">
                <input
                  id="domain-link-url"
                  type="text"
                  readOnly
                  value={issued.url}
                  aria-describedby="domain-link-url-hint"
                />
                <button type="button" className="button" onClick={() => void copy(issued.url)}>
                  {t('domain_link.admin_copy')}
                </button>
              </div>
              <p className="muted" id="domain-link-url-hint">
                {t('domain_link.admin_created_hint')}
              </p>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

// ------------------------------------------------------ il contrasto, a parole

/**
 * Cosa la piattaforma userà davvero, detto in italiano e non in quadratini.
 *
 * Le due palette hanno due storie diverse e vanno raccontate separate: in
 * chiaro il colore si corregge quando **il testo sopra** non si legge, in
 * scuro quando **il pulsante** non si stacca dalla pagina. Sono due soglie
 * diverse — 4,5:1 per il testo, 3:1 per un componente — e dirle insieme
 * farebbe sembrare arbitrario che il colore cambi in un tema e non nell'altro.
 */
function PaletteReport({ view, chosen }: { view: PaletteView; chosen: string }) {
  const { t, i18n } = useTranslation();
  const ratio = (value: number): string =>
    new Intl.NumberFormat(i18n.resolvedLanguage, { maximumFractionDigits: 1 }).format(value);

  return (
    <div className="stack stack--tight">
      <h4>{t('branding.palette_light')}</h4>
      <p>{t('branding.palette_chosen', { color: chosen })}</p>
      {view.adjusted.light ? (
        <p className="banner banner--warn">
          {t('branding.palette_corrected_light', { color: view.palette.accent })}
        </p>
      ) : (
        <p>{t('branding.palette_same')}</p>
      )}
      <p>
        {t('branding.palette_contrast', {
          text: view.palette.accentText,
          ratio: ratio(view.contrast.light),
        })}
      </p>

      <h4>{t('branding.palette_dark')}</h4>
      <p className="muted">{t('branding.palette_dark_why')}</p>
      {view.adjusted.dark ? (
        <p>{t('branding.palette_adjusted_dark', { color: view.palette.dark.accent })}</p>
      ) : (
        <p>{t('branding.palette_used', { color: view.palette.dark.accent })}</p>
      )}
      <p>
        {t('branding.palette_contrast', {
          text: view.palette.dark.accentText,
          ratio: ratio(view.contrast.dark),
        })}
      </p>
    </div>
  );
}

// ------------------------------------------------------------- l'anteprima

/**
 * Il pannello com'è sul sito, in miniatura.
 *
 * Non è un widget vero e non ci prova: è **testo dentro i colori che si
 * stanno scegliendo**, con la stessa gerarchia del pannello. Quello che deve
 * rispondere è "come sarà", e le due domande a cui risponde davvero sono le
 * uniche che qualcuno si fa prima di salvare: il mio colore si vede, e i miei
 * testi ci stanno dentro.
 *
 * Sotto i testi dell'ente restano le frasi che l'ente non cambia. Sono lì
 * apposta: chi personalizza deve vedere che il "non è un servizio di
 * emergenza" e il numero unico non spariscono, altrimenti il primo a
 * dimenticarsene è chi configura.
 */
function Preview({
  dark,
  title,
  state,
  draft,
  locale,
  palette,
  name,
}: {
  dark: boolean;
  title: string;
  state: BrandingState;
  draft: Draft;
  locale: string;
  palette: WidgetPalette;
  name: string;
}) {
  const { t } = useTranslation();
  const colors = dark ? palette.dark : palette;

  const text = (key: string): string =>
    draft.texts[locale]?.[key] || (state.defaults[locale]?.[key] ?? '');

  const launcher = text('widget.title') || name;

  return (
    <figure className="preview-wrap">
      <figcaption className="muted">{title}</figcaption>
      <div
        className={dark ? 'preview preview--dark' : 'preview'}
        style={
          {
            // Gli unici valori inline della console: vengono dai dati, non dal
            // foglio di stile, e sono precisamente ciò che si sta scegliendo.
            '--wp-accent': colors.accent,
            '--wp-accent-text': colors.accentText,
            '--wp-accent-subtle': colors.accentSubtle,
            '--wp-accent-on-surface': colors.accentOnSurface,
          } as React.CSSProperties
        }
      >
        <p className="preview__launcher">
          {state.logoUrl ? (
            <img className="preview__logo" src={state.logoUrl} alt="" aria-hidden="true" />
          ) : (
            <span className="preview__mark" aria-hidden="true" />
          )}
          {draft.layout === 'button_only' ? null : (
            <span className="preview__launcher-text">
              <span>{launcher}</span>
              {draft.layout === 'extended' ? (
                <span className="preview__status">{t('branding.preview_status')}</span>
              ) : null}
            </span>
          )}
        </p>

        <div className="preview__panel">
          <p className="preview__org">{name}</p>
          <p className="preview__title">{text('widget.title')}</p>
          {text('widget.subtitle') ? (
            <p className="preview__sub">{text('widget.subtitle')}</p>
          ) : null}

          <p className="preview__ctas">
            {draft.channels.map((channel) => (
              <span className="preview__cta" key={channel}>
                {text(CHANNEL_TEXTS[channel] ?? '')}
              </span>
            ))}
          </p>

          {text('widget.open.hint') ? (
            <p className="preview__note">{text('widget.open.hint')}</p>
          ) : null}

          {(state.safety[locale] ?? []).map((sentence) => (
            <p className="preview__safety" key={sentence}>
              {sentence}
            </p>
          ))}

          {state.emergency.fixed.map((entry) => (
            <p className="preview__number" key={entry.number}>
              {entry.number}
            </p>
          ))}
          {draft.contacts
            .filter((contact) => contact.number.length > 0)
            .map((contact) => (
              <p className="preview__number" key={contact.id}>
                {contact.labels[locale] ? `${contact.labels[locale]} · ` : ''}
                {contact.number}
              </p>
            ))}
        </div>
      </div>
    </figure>
  );
}

/** `#abc` e `abc` sono la stessa scelta: la casella dei colori ne accetta una sola. */
function normalize(value: string): string {
  const hex = value.trim().replace(/^#/, '');
  const full =
    hex.length === 3
      ? hex
          .split('')
          .map((char) => char + char)
          .join('')
      : hex;
  return `#${full.toLowerCase()}`;
}
